import { AccountCircle, Campaign, ChangeCircle, ChevronLeft, ChevronRight, CrisisAlert, Event, Forum, Handyman, Home, Inventory, Inventory2, LibraryBooks, LocalParking, MeetingRoom, Newspaper, School, Summarize, Task } from '@mui/icons-material';
import { Box, Drawer, List, ListItemButton, ListItemText, Paper, Tooltip, Typography, useTheme, SvgIconTypeMap } from '@mui/material';
import React, { useEffect } from 'react';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { useNavigate, useParams } from 'react-router-dom';
import { ManagementMenuItems, OwnerMenuItems } from '../../Utils/Constants';
import { useAuth } from '../../context/authContext';
import { UserType } from '../../Types/UnitFeatureTypes';


interface MenuOptionsProps {
    menuWidth: number;
    setMenuWidth: (width: number) => void;
}

const MenuOptions: React.FC<MenuOptionsProps> = ({
    menuWidth,
    setMenuWidth
}) => {

    const theme = useTheme();
    const navigate = useNavigate();
    const { user} = useAuth();
    const IS_MANAGER = user?.type === UserType.MANAGER;
    const [activeMenu, setActiveMenu] = React.useState<string>('');

    const MenuItems = [menuWidth === 250 ? 'Hide' : 'Show', ...(IS_MANAGER ? ManagementMenuItems : OwnerMenuItems)];
    const MenuIcons: { [key: string]: OverridableComponent<SvgIconTypeMap<{}, "svg">> } = {
      'Hide': ChevronLeft,
      'Show': ChevronRight,
      'Dashboard': Home,
      'Events': Event,
      'Library': LibraryBooks,
      'To Dos': Task,
      'My Account': AccountCircle,
      'Service Requests': Handyman,
      'Visitor Parking': LocalParking,
      'Bookings': Event,
      'Violations': CrisisAlert,
      'Announcements': Campaign,
      'Unit File': MeetingRoom,
      'Reports': Summarize,
      'Packages': Inventory2,
      'Classifieds': Newspaper,
      'Discussion Forum': Forum,
      'Training Library': School,
    };
    const menuOnClick: { [key: string]: () => void } = {
      'Hide': () => setMenuWidth(menuWidth === 250 ? 50 : 250),
      'Show': () => setMenuWidth(menuWidth === 250 ? 50 : 250),
      'Dashboard': () => navigate('/'),
      'Service Requests': () => navigate('/serviceRequests'),
      'Visitor Parking': () => navigate('/visitorParkings'),
      'Bookings': () => navigate('/bookings'),
      'Violations': () => navigate('/violations'),
      'Announcements': () => navigate('/announcements'),
      'Reports': () => navigate('/reports'),
      'Events': () => navigate('/events'),
      'Library': () => navigate('/library'),
      'To Dos': () => navigate('/todos'),
      'My Account': () => navigate('/account'),
      'Unit File': () => navigate('/unitFile'),
      'Packages': () => navigate('/packages'),
      'Classifieds': () => navigate('/classifieds'),
      'Discussion Forum': () => navigate('/discussionForum'),
      'Training Library': () => navigate('/trainingLibrary'),
    };

    const getTabByPath = () => {
        switch (window.location.pathname.split('/')[1]) {
            case 'serviceRequests':
                setActiveMenu('Service Requests');
                break;
            case 'visitorParkings':
                setActiveMenu('Visitor Parking');
                break;
            case 'bookings':
                setActiveMenu('Bookings');
                break;
            case 'violations':
                setActiveMenu('Violations');
                break;
            case 'announcements':
                setActiveMenu('Announcements');
                break;
            case 'reports':
                setActiveMenu('Reports');
                break;
            case 'events':
                setActiveMenu('Events');
                break;
            case 'library':
                setActiveMenu('Library');
                break;
            case 'todos':
                setActiveMenu('To Dos');
                break;
            case 'account':
                setActiveMenu('My Account');
                break;
            case 'unitFile':
                setActiveMenu('Unit File');
                break;
            case 'packages':
                setActiveMenu('Package Management');
                break;
            case 'classifieds':
                setActiveMenu('Classifieds');
                break;
            case 'discussionForum':
                setActiveMenu('Discussion Forum');
                break;
            case 'trainingLibrary':
                setActiveMenu('Training Library');
                break;
            default:
                setActiveMenu('Dashboard');
                break;
        }
    };

    useEffect(() => {
        getTabByPath();
    }, []);

    const handleActiveMenu = (menu: string) => {
        if (menu === 'Hide' || menu === 'Show') {
            return;
        }
        setActiveMenu(menu);
    };

    const buildMenu = () => {
        return (
        <Drawer variant='permanent' elevation={5} sx={{ borderRadius: 3, textAlign: 'center',
            '& .MuiDrawer-paper': {
                marginTop: 12.2,
                height: 'calc(100% - 97px)',
                width: menuWidth,
                borderTopRightRadius: 20,
                animation: 'width 0.5s',
                transition: 'width 0.3s',
                background: theme.palette.primary.main,
            }
         }}>
            <List>
                {MenuItems.map((item: string, index: number) => (
                <Tooltip title={item} key={index}>
                    <ListItemButton 
                        sx={{
                            padding: 0.7, 
                            backgroundColor: activeMenu === item ? '#fabd2e' : theme.palette.primary.main, 
                            '&:hover': { backgroundColor: activeMenu === item ? '#fabd2e' : 'default' },
                            '&:focus': { backgroundColor: activeMenu === item ? '#fabd2e' : 'default' }
                        }} 
                        key={index} 
                        onClick={() => { 
                            menuOnClick[item as keyof typeof MenuIcons](); 
                            handleActiveMenu(item); 
                        }}
                    >
                        <ListItemText 
                            primary={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {React.createElement(MenuIcons[item as keyof typeof MenuIcons], { sx: { color: 'white', fontSize: 31 } })}
                                    {menuWidth === 250 && <Typography variant="h6" sx={{ color: 'white', fontWeight: 500, marginLeft: 1 }}>{item}</Typography>}
                                </Box>
                            } 
                        />
                    </ListItemButton>
                </Tooltip>
                ))}
            </List>
        </Drawer>
        );
    };
    
    return (
        buildMenu()
    );
}

export default MenuOptions;